/**
 * Geocoding & distance utilities for delivery radius validation.
 * Uses free Nominatim API (OpenStreetMap) for geocoding — no API key needed.
 */

export interface LatLng {
  lat: number;
  lng: number;
}

/**
 * Geocode an address using Nominatim structured search with progressive fallbacks.
 */
export async function geocodeAddress(address: string, structured?: {
  street?: string;
  city?: string;
  state?: string;
  postalcode?: string;
  country?: string;
}): Promise<LatLng | null> {
  const headers = { "User-Agent": "ViaMenu/1.0" };

  // If structured params provided, try structured search first
  if (structured) {
    const attempts = [
      // 1) Full structured search
      buildStructuredUrl(structured),
      // 2) Without street number (just street name + city)
      buildStructuredUrl({ ...structured, street: structured.street?.replace(/,?\s*\d+\s*$/, '').replace(/^\d+\s*,?\s*/, '') }),
      // 3) Just city + state
      buildStructuredUrl({ city: structured.city, state: structured.state, country: structured.country }),
      // 4) Postalcode only
      structured.postalcode ? `https://nominatim.openstreetmap.org/search?format=json&postalcode=${encodeURIComponent(structured.postalcode)}&countrycodes=br&limit=1` : null,
    ].filter(Boolean) as string[];

    for (const url of attempts) {
      const result = await tryFetch(url, headers);
      if (result) return result;
    }
  }

  // Fallback: free-text search with progressively simpler queries
  const parts = address.split(",").map(p => p.trim()).filter(Boolean);
  const attempts = [
    address, // full address
    parts.length > 2 ? parts.slice(0, 3).join(", ") + ", Brasil" : null, // first 3 parts
    parts.length > 1 ? `${parts[0]}, ${parts[parts.length - 2]}, ${parts[parts.length - 1]}` : null, // street + city + state
    parts.length > 1 ? parts.slice(-2).join(", ") + ", Brasil" : null, // just city + state
  ].filter(Boolean) as string[];

  for (const q of attempts) {
    const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(q)}&countrycodes=br&limit=1`;
    const result = await tryFetch(url, headers);
    if (result) return result;
  }

  return null;
}

function buildStructuredUrl(params: {
  street?: string;
  city?: string;
  state?: string;
  postalcode?: string;
  country?: string;
}): string {
  const parts: string[] = [];
  if (params.street) parts.push(`street=${encodeURIComponent(params.street)}`);
  if (params.city) parts.push(`city=${encodeURIComponent(params.city)}`);
  if (params.state) parts.push(`state=${encodeURIComponent(params.state)}`);
  if (params.postalcode) parts.push(`postalcode=${encodeURIComponent(params.postalcode.replace(/\D/g, ''))}`);
  parts.push(`country=${encodeURIComponent(params.country || 'Brazil')}`);
  parts.push('format=json');
  parts.push('limit=1');
  return `https://nominatim.openstreetmap.org/search?${parts.join('&')}`;
}

async function tryFetch(url: string, headers: Record<string, string>): Promise<LatLng | null> {
  try {
    const res = await fetch(url, { headers });
    if (!res.ok) return null;
    const data = await res.json();
    if (!data.length) return null;
    return { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
  } catch {
    return null;
  }
}

/**
 * Haversine distance between two lat/lng points, in km.
 */
export function haversineDistanceKm(a: LatLng, b: LatLng): number {
  const R = 6371;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const sinLat = Math.sin(dLat / 2);
  const sinLng = Math.sin(dLng / 2);
  const h = sinLat * sinLat + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * sinLng * sinLng;
  return R * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

function toRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

/**
 * Check if a customer address is within the restaurant's delivery radius.
 */
export async function isWithinDeliveryRadius(
  restaurantCoords: LatLng,
  customerAddress: string,
  radiusKm: number
): Promise<{ within: boolean; distance: number | null; customerCoords: LatLng | null }> {
  const customerCoords = await geocodeAddress(customerAddress);
  if (!customerCoords) return { within: false, distance: null, customerCoords: null };
  const distance = haversineDistanceKm(restaurantCoords, customerCoords);
  return { within: distance <= radiusKm, distance: Math.round(distance * 10) / 10, customerCoords };
}
